<template>
  <div class="login-container">
    <!-- 浮动装饰元素 -->
    <div class="floating-element"></div>
    <div class="floating-element"></div>
    <div class="floating-element"></div>
    <div class="floating-element"></div>
    
    <div class="login-card">
      <div class="login-header">
        <div class="logo">
          <div class="logo-icon">
            <i class="icon-shield">🛡️</i>
          </div>
          <h2 class="login-title">云安管家</h2>
          <p class="login-subtitle">智能安全设备管理平台</p>
        </div>
      </div>
      
      <!-- 登录/注册切换标签 -->
      <el-tabs v-model="activeTab" class="login-tabs" @tab-click="handleTabChange">
        <el-tab-pane label="登录" name="login">
          <div class="tab-description">
            <p class="description-text">登录需要已绑定家庭的账号</p>
          </div>
          <el-form 
            :model="loginForm" 
            :rules="loginRules" 
            ref="loginFormRef"
            class="login-form"
            @submit.prevent="handleLogin"
          >
            <el-form-item prop="phone">
              <el-input
                v-model="loginForm.phone"
                placeholder="手机号"
                size="large"
                maxlength="11"
                clearable
              >
                <template #prefix>
                  <el-icon><Phone /></el-icon>
                </template>
              </el-input>
            </el-form-item>
            
            <el-form-item prop="password">
              <el-input
                v-model="loginForm.password"
                type="password"
                placeholder="密码"
                size="large"
                show-password
              >
                <template #prefix>
                  <el-icon><Lock /></el-icon>
                </template>
              </el-input>
            </el-form-item>
            
            <el-form-item>
              <el-button 
                type="primary" 
                size="large" 
                style="width: 100%"
                :loading="loginLoading"
                @click="handleLogin"
              >
                登录
              </el-button>
            </el-form-item>
          </el-form>
        </el-tab-pane>
        
        <el-tab-pane label="注册" name="register">
          <div class="tab-description">
            <p class="description-text">首次使用？创建新家庭账号</p>
          </div>
          <el-form 
            :model="registerForm" 
            :rules="registerRules" 
            ref="registerFormRef"
            class="register-form"
            @submit.prevent="handleRegister"
          >
            <el-form-item prop="familyName">
              <el-input
                v-model="registerForm.familyName"
                placeholder="家庭名称"
                size="large"
                maxlength="50"
                clearable
              >
                <template #prefix>
                  <el-icon><House /></el-icon>
                </template>
              </el-input>
            </el-form-item>
            
            <el-form-item prop="ownerName">
              <el-input
                v-model="registerForm.ownerName"
                placeholder="户主姓名"
                size="large"
                maxlength="20"
                clearable
              >
                <template #prefix>
                  <el-icon><User /></el-icon>
                </template>
              </el-input>
            </el-form-item>
            
            <el-form-item prop="phone">
              <el-input
                v-model="registerForm.phone"
                placeholder="手机号"
                size="large"
                maxlength="11"
                clearable
              >
                <template #prefix>
                  <el-icon><Phone /></el-icon>
                </template>
              </el-input>
            </el-form-item>
            
            <el-form-item prop="password">
              <el-input
                v-model="registerForm.password"
                type="password"
                placeholder="密码"
                size="large"
                show-password
              >
                <template #prefix>
                  <el-icon><Lock /></el-icon>
                </template>
              </el-input>
            </el-form-item>
            
            <el-form-item prop="confirmPassword">
              <el-input
                v-model="registerForm.confirmPassword"
                type="password"
                placeholder="确认密码"
                size="large"
                show-password
              >
                <template #prefix>
                  <el-icon><Lock /></el-icon>
                </template>
              </el-input>
            </el-form-item>
            
            <el-form-item prop="address">
              <el-input
                v-model="registerForm.address"
                placeholder="家庭地址"
                size="large"
                type="textarea"
                :rows="2"
                maxlength="200"
              >
                <template #prefix>
                  <el-icon><Location /></el-icon>
                </template>
              </el-input>
            </el-form-item>
            
            <el-form-item>
              <el-button 
                type="primary" 
                size="large" 
                style="width: 100%"
                :loading="registerLoading"
                @click="handleRegister"
              >
                注册家庭
              </el-button>
            </el-form-item>
          </el-form>
        </el-tab-pane>
      </el-tabs>

      <!-- 跳过登录按钮 -->
      <div class="skip-login">
        <el-button
          text
          size="large"
          @click="skipLogin"
          class="skip-btn"
        >
          暂不登录，先看看
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { authAPI } from '../api/auth'
import { User, Lock, Phone, House, Location } from '@element-plus/icons-vue'

export default {
  name: 'Login',
  components: {
    User, Lock, Phone, House, Location
  },
  setup() {
    const router = useRouter()
    const loginFormRef = ref()
    const registerFormRef = ref()
    const loginLoading = ref(false)
    const registerLoading = ref(false)
    const activeTab = ref('login')
    
    // 登录表单
    const loginForm = reactive({
      phone: '',
      password: ''
    })
    
    // 注册表单
    const registerForm = reactive({
      familyName: '',
      ownerName: '',
      phone: '',
      password: '',
      confirmPassword: '',
      address: ''
    })
    
    // 手机号验证规则
    const validatePhone = (rule, value, callback) => {
      const phoneRegex = /^1[3-9]\d{9}$/
      if (!value) {
        callback(new Error('请输入手机号'))
      } else if (!phoneRegex.test(value)) {
        callback(new Error('请输入正确的手机号格式'))
      } else {
        callback()
      }
    }
    
    // 确认密码验证规则
    const validateConfirmPassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请确认密码'))
      } else if (value !== registerForm.password) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }
    
    // 登录表单验证规则
    const loginRules = {
      phone: [
        { validator: validatePhone, trigger: 'blur' }
      ],
      password: [
        { required: true, message: '请输入密码', trigger: 'blur' },
        { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
      ]
    }
    
    // 注册表单验证规则
    const registerRules = {
      familyName: [
        { required: true, message: '请输入家庭名称', trigger: 'blur' },
        { min: 2, max: 50, message: '家庭名称长度为2-50个字符', trigger: 'blur' }
      ],
      ownerName: [
        { required: true, message: '请输入户主姓名', trigger: 'blur' },
        { min: 2, max: 20, message: '姓名长度为2-20个字符', trigger: 'blur' }
      ],
      phone: [
        { validator: validatePhone, trigger: 'blur' }
      ],
      password: [
        { required: true, message: '请输入密码', trigger: 'blur' },
        { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
      ],
      confirmPassword: [
        { validator: validateConfirmPassword, trigger: 'blur' }
      ],
      address: [
        { required: true, message: '请输入家庭地址', trigger: 'blur' },
        { min: 5, max: 200, message: '地址长度为5-200个字符', trigger: 'blur' }
      ]
    }
    
    // 标签切换处理
    const handleTabChange = (tab) => {
      activeTab.value = tab.name
    }
    
    // 登录处理
    const handleLogin = async () => {
      if (!loginFormRef.value) return
      
      await loginFormRef.value.validate(async (valid) => {
        if (valid) {
          loginLoading.value = true
          try {
            const response = await authAPI.login({
              phone: loginForm.phone,
              password: loginForm.password
            })
            
            if (response.success) {
              // 检查用户是否有绑定的家庭
              if (response.data.isNewUser || !response.data.family) {
                ElMessage.warning({
                  message: '账号未绑定家庭，无法登录\n请选择以下方式之一：\n1. 使用"注册"功能创建新家庭\n2. 联系管理员将您加入现有家庭',
                  duration: 8000,
                  showClose: true
                })
                
                // 自动切换到注册页面，方便用户直接注册
                setTimeout(() => {
                  activeTab.value = 'register'
                  registerForm.phone = loginForm.phone // 预填手机号
                }, 2000)
                
                console.log('Login blocked: User has no family binding', {
                  isNewUser: response.data.isNewUser,
                  family: response.data.family
                })
                return
              }
              
              ElMessage.success('登录成功!')
              // 存储用户信息和家庭信息
              localStorage.setItem('user', JSON.stringify(response.data.user))
              localStorage.setItem('family', JSON.stringify(response.data.family))
              localStorage.setItem('token', response.data.token)
              
              // 打印调试信息
              console.log('Login success, stored user:', response.data.user)
              console.log('Login success, stored family:', response.data.family)
              
              router.push('/dashboard')
            } else {
              ElMessage.error(response.message || '登录失败')
            }
          } catch (error) {
            console.error('Login error:', error)
            ElMessage.error('网络错误，请重试')
          } finally {
            loginLoading.value = false
          }
        }
      })
    }
    
    // 跳过登录
    const skipLogin = () => {
      router.push('/welcome')
    }

    // 注册处理
    const handleRegister = async () => {
      if (!registerFormRef.value) return
      
      await registerFormRef.value.validate(async (valid) => {
        if (valid) {
          registerLoading.value = true
          try {
            const response = await authAPI.register({
              familyName: registerForm.familyName,
              ownerName: registerForm.ownerName,
              phone: registerForm.phone,
              password: registerForm.password,
              address: registerForm.address
            })
            
            if (response.success) {
              ElMessage.success('注册成功! 请登录')
              // 切换到登录标签并填充手机号
              activeTab.value = 'login'
              loginForm.phone = registerForm.phone
              // 清空注册表单
              Object.keys(registerForm).forEach(key => {
                registerForm[key] = ''
              })
            } else {
              ElMessage.error(response.message || '注册失败')
            }
          } catch (error) {
            console.error('Register error:', error)
            ElMessage.error('网络错误，请重试')
          } finally {
            registerLoading.value = false
          }
        }
      })
    }
    
    return {
      activeTab,
      loginForm,
      registerForm,
      loginRules,
      registerRules,
      loginFormRef,
      registerFormRef,
      loginLoading,
      registerLoading,
      handleTabChange,
      handleLogin,
      handleRegister,
      skipLogin
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #b3e5fc 0%, #e0f7fe 100%);
  position: relative;
  overflow: hidden;
}

/* 背景装饰 */
.login-container::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: 
    radial-gradient(circle, rgba(255,255,255,0.4) 1px, transparent 1px),
    radial-gradient(circle, rgba(79, 172, 254, 0.2) 1px, transparent 1px);
  background-size: 60px 60px, 40px 40px;
  background-position: 0 0, 25px 25px;
  animation: backgroundMove 25s linear infinite;
}

.login-container::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 15% 85%, rgba(79, 172, 254, 0.25) 0%, rgba(79, 172, 254, 0.05) 40%, transparent 70%),
    radial-gradient(circle at 85% 15%, rgba(0, 242, 254, 0.25) 0%, rgba(0, 242, 254, 0.05) 40%, transparent 70%),
    radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.03) 30%, transparent 60%),
    radial-gradient(circle at 70% 70%, rgba(79, 172, 254, 0.15) 0%, transparent 50%);
  animation: floatingBubbles 12s ease-in-out infinite;
  pointer-events: none;
}

/* 浮动气泡动画 */
@keyframes floatingBubbles {
  0%, 100% { 
    transform: translateY(0px) scale(1) rotate(0deg);
    opacity: 0.8;
  }
  25% { 
    transform: translateY(-40px) scale(1.2) rotate(90deg);
    opacity: 1;
  }
  50% { 
    transform: translateY(-20px) scale(0.8) rotate(180deg);
    opacity: 0.6;
  }
  75% { 
    transform: translateY(-60px) scale(1.1) rotate(270deg);
    opacity: 0.9;
  }
}

/* 点状背景移动动画 */
@keyframes backgroundMove {
  0% { transform: translate(0, 0) rotate(0deg); }
  100% { transform: translate(50px, 50px) rotate(360deg); }
}

/* 添加一些随机的小元素 */
.login-container .floating-element {
  position: absolute;
  width: 8px;
  height: 8px;
  background: rgba(79, 172, 254, 0.6);
  border-radius: 50%;
  pointer-events: none;
  box-shadow: 0 0 10px rgba(79, 172, 254, 0.3);
}

.login-container .floating-element:nth-child(1) {
  top: 10%;
  left: 20%;
  animation: float1 8s ease-in-out infinite;
}

.login-container .floating-element:nth-child(2) {
  top: 70%;
  right: 15%;
  animation: float2 10s ease-in-out infinite;
  background: rgba(0, 242, 254, 0.6);
  box-shadow: 0 0 10px rgba(0, 242, 254, 0.3);
  width: 10px;
  height: 10px;
}

.login-container .floating-element:nth-child(3) {
  bottom: 20%;
  left: 15%;
  animation: float3 12s ease-in-out infinite;
  background: rgba(255, 255, 255, 0.7);
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.4);
  width: 6px;
  height: 6px;
}

.login-container .floating-element:nth-child(4) {
  top: 30%;
  right: 30%;
  animation: float4 9s ease-in-out infinite;
  background: rgba(79, 172, 254, 0.5);
  box-shadow: 0 0 12px rgba(79, 172, 254, 0.3);
  width: 12px;
  height: 12px;
}

@keyframes float1 {
  0%, 100% { 
    transform: translateY(0px) translateX(0px) scale(1);
    opacity: 0.6;
  }
  33% { 
    transform: translateY(-40px) translateX(20px) scale(1.5);
    opacity: 1;
  }
  66% { 
    transform: translateY(20px) translateX(-15px) scale(0.8);
    opacity: 0.8;
  }
}

@keyframes float2 {
  0%, 100% { 
    transform: translateY(0px) translateX(0px) rotate(0deg);
    opacity: 0.6;
  }
  25% { 
    transform: translateY(-30px) translateX(-20px) rotate(90deg);
    opacity: 1;
  }
  50% { 
    transform: translateY(10px) translateX(30px) rotate(180deg);
    opacity: 0.8;
  }
  75% { 
    transform: translateY(-20px) translateX(-10px) rotate(270deg);
    opacity: 0.9;
  }
}

@keyframes float3 {
  0%, 100% { 
    transform: translateY(0px) translateX(0px) scale(1);
    opacity: 0.7;
  }
  30% { 
    transform: translateY(-50px) translateX(16px) scale(2);
    opacity: 1;
  }
  60% { 
    transform: translateY(30px) translateX(-24px) scale(0.5);
    opacity: 0.6;
  }
}

@keyframes float4 {
  0%, 100% { 
    transform: translateY(0px) translateX(0px) rotate(0deg) scale(1);
    opacity: 0.5;
  }
  50% { 
    transform: translateY(-36px) translateX(24px) rotate(360deg) scale(1.8);
    opacity: 1;
  }
}

.login-card {
  width: 480px;
  max-width: 90vw;
  padding: 0;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  overflow-y: auto;
  backdrop-filter: blur(20px);
  position: relative;
  z-index: 10;
  animation: cardSlideIn 0.8s ease-out;
}

@keyframes cardSlideIn {
  0% { transform: translateY(50px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
}

.login-header {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  padding: 40px 40px 30px;
  border-radius: 20px 20px 0 0;
  text-align: center;
  color: white;
  position: relative;
  overflow: hidden;
}

.login-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 20"><defs><radialGradient id="a" cx="50%" cy="40%"><stop offset="0%" stop-color="rgba(255,255,255,0.1)" /><stop offset="100%" stop-color="rgba(255,255,255,0)" /></radialGradient></defs><rect fill="url(%23a)" width="100%" height="100%"/></svg>');
  opacity: 0.8;
}

.logo {
  position: relative;
  z-index: 1;
}

.logo-icon {
  font-size: 60px;
  margin-bottom: 16px;
  display: inline-block;
  animation: logoFloat 3s ease-in-out infinite;
}

@keyframes logoFloat {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

.login-title {
  margin: 0 0 8px 0;
  font-size: 32px;
  font-weight: 700;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  letter-spacing: 1px;
}

.login-subtitle {
  margin: 0;
  font-size: 16px;
  opacity: 0.9;
  font-weight: 300;
}

.login-tabs {
  padding: 0 40px;
  background: white;
}

.login-form,
.register-form {
  padding: 30px 40px 40px;
  background: white;
  border-radius: 0 0 20px 20px;
}

/* 标签页样式优化 */
:deep(.el-tabs__header) {
  margin-bottom: 0;
  background: white;
  border-radius: 0 0 20px 20px;
  padding-top: 20px;
  border-bottom: 2px solid #f5f7fa;
}

:deep(.el-tabs__content) {
  overflow: visible;
  padding: 0;
}

:deep(.el-tabs__item) {
  font-size: 18px;
  font-weight: 600;
  color: #606266;
  height: 50px;
  line-height: 50px;
  padding: 0 30px;
}

:deep(.el-tabs__item.is-active) {
  color: #4facfe;
  font-weight: 700;
}

:deep(.el-tabs__active-bar) {
  background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%);
  height: 3px;
}

:deep(.el-tabs__nav-wrap::after) {
  height: 0;
}

/* 输入框样式美化 */
:deep(.el-input__wrapper) {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(79, 172, 254, 0.1);
  border: 2px solid #f0f2f5;
  transition: all 0.3s ease;
}

:deep(.el-input__wrapper:hover) {
  border-color: #4facfe;
  box-shadow: 0 4px 12px rgba(79, 172, 254, 0.2);
}

:deep(.el-input.is-focus .el-input__wrapper) {
  border-color: #4facfe;
  box-shadow: 0 0 0 3px rgba(79, 172, 254, 0.1);
}

:deep(.el-input__inner) {
  font-size: 16px;
  padding: 12px 15px;
}

:deep(.el-input__prefix) {
  left: 15px;
}

:deep(.el-input__prefix .el-icon) {
  color: #4facfe;
  font-size: 18px;
}

/* 按钮样式美化 */
:deep(.el-button--primary) {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  height: 50px;
  box-shadow: 0 4px 15px rgba(79, 172, 254, 0.3);
  transition: all 0.3s ease;
}

:deep(.el-button--primary:hover) {
  background: linear-gradient(135deg, #4098fe 0%, #00d4fe 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(79, 172, 254, 0.4);
}

:deep(.el-button--primary:active) {
  transform: translateY(0);
}

:deep(.el-button.is-loading) {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

/* 表单项间距调整 */
.register-form .el-form-item {
  margin-bottom: 20px;
}

.login-form .el-form-item {
  margin-bottom: 24px;
}

.login-form .el-form-item:last-child,
.register-form .el-form-item:last-child {
  margin-bottom: 0;
}

/* 文本域样式 */
:deep(.el-textarea .el-input__wrapper) {
  min-height: 80px;
  padding: 8px 15px;
}

:deep(.el-textarea__inner) {
  resize: vertical;
  min-height: 60px;
  font-size: 16px;
  line-height: 1.5;
}

/* 错误提示样式 */
:deep(.el-form-item__error) {
  color: #f56c6c;
  font-size: 14px;
  margin-top: 8px;
}

/* 跳过登录按钮 */
.skip-login {
  text-align: center;
  padding: 0 40px 30px;
  border-radius: 0 0 20px 20px;
  background: white;
}

.skip-btn {
  font-size: 16px;
  color: #909399;
  transition: all 0.3s ease;
  padding: 8px 20px;
}

.skip-btn:hover {
  color: #4facfe;
  background: rgba(79, 172, 254, 0.08);
  transform: translateY(-1px);
}

/* 响应式设计 */
@media (max-width: 600px) {
  .login-card {
    width: 95vw;
    margin: 20px;
  }
  
  .login-header {
    padding: 30px 30px 25px;
  }
  
  .login-title {
    font-size: 26px;
  }
  
  .login-subtitle {
    font-size: 14px;
  }
  
  .login-form,
  .register-form {
    padding: 25px 30px 35px;
  }
  
  .login-tabs {
    padding: 0 30px;
  }
  
  .tab-description {
    text-align: center;
    margin-bottom: 15px;
  }
  
  .description-text {
    color: #666;
    font-size: 14px;
    margin: 0;
    padding: 0;
  }
  
  :deep(.el-tabs__item) {
    font-size: 16px;
    padding: 0 20px;
  }
  
  :deep(.el-input__inner) {
    font-size: 15px;
  }
  
  :deep(.el-button--primary) {
    font-size: 16px;
    height: 48px;
  }
}
</style>